import { useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../../lib/api';
import { sendFile } from '../../lib/upload';
import { dateTime, fileSize, plural } from '../../lib/format';
import { useLoad, useProjectEvents } from '../../lib/hooks';
import type { Asset, Item, Project } from '../../lib/types';
import { useAuth } from '../../state/auth';
import { Icon, type IconName } from '../../components/Icon';
import { ConfirmDialog, Empty, ErrorState, Menu, Spinner, type MenuEntry } from '../../components/ui';
import { useToast } from '../../components/toast';

const iconFor = (asset: Asset): IconName => (asset.media === 'image' ? 'image' : asset.media === 'video' ? 'video' : 'file');

/** The project's shared drop box: clients hand over logos, copy, footage; the team hands back deliverables. */
export function FilesTab({ project, canComment, canManage, onChanged }: { project: Project; canComment: boolean; canManage: boolean; onChanged: () => void }) {
  const { user, meta } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, loading, error: loadError, reload } = useLoad(() => api.get<{ files: Asset[] }>(`/api/projects/${project.id}/files`), [project.id]);
  const [over, setOver] = useState(false);
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState<{ count: number; fraction: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Asset | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useProjectEvents(project.id, (event) => {
    if (event.type === 'files.updated' || event.type === 'resync') void reload(true);
  });

  const files = data?.files ?? [];

  const send = async (list: File[]) => {
    if (!list.length) return;
    if (progress) {
      toast.error('An upload is still running. Add more files when it finishes.');
      return;
    }
    const limit = (meta?.maxUploadMb ?? 1024) * 1024 * 1024;
    const tooBig = list.find((file) => file.size > limit);
    if (tooBig) {
      setError(`"${tooBig.name}" is larger than the ${meta?.maxUploadMb ?? 1024} MB limit.`);
      return;
    }
    setError(null);
    setProgress({ count: list.length, fraction: 0 });
    let sent = 0;
    let current = list[0];
    try {
      // One file at a time keeps progress honest and lets big videos go up in chunks.
      const total = list.reduce((sum, file) => sum + file.size, 0) || 1;
      let done = 0;
      for (const file of list) {
        current = file;
        await sendFile({
          projectId: project.id,
          purpose: 'file',
          file,
          note,
          chunkBytes: meta?.uploadChunkBytes ?? 32 * 1024 * 1024,
          onProgress: (fraction) => setProgress({ count: list.length, fraction: (done + fraction * file.size) / total }),
        });
        done += file.size;
        sent += 1;
      }
      setNote('');
      toast.show(`${plural(list.length, 'file')} uploaded`);
      await reload(true);
      onChanged();
    } catch (err) {
      // Say which file stopped the batch and what happened to the rest.
      const rest = list.length - sent - 1;
      setError(`"${current.name}" was not uploaded: ${errorMessage(err)}${list.length > 1 ? ` ${plural(sent, 'file')} went through${rest > 0 ? `, ${rest} after it ${rest === 1 ? 'was' : 'were'} not sent` : ''}.` : ''}`);
      if (sent > 0) {
        void reload(true);
        onChanged();
      }
    } finally {
      setProgress(null);
    }
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setOver(false);
    // A dropped folder arrives as an unreadable zero-byte "file".
    const hasFolder = Array.from(event.dataTransfer.items || []).some((entry) => entry.webkitGetAsEntry?.()?.isDirectory);
    if (hasFolder) {
      setError('Folders cannot be uploaded. Open the folder and drop the files inside it, or zip it first.');
      return;
    }
    void send(Array.from(event.dataTransfer.files));
  };

  const startReview = async (asset: Asset) => {
    try {
      const { item } = await api.post<{ item: Item }>(`/api/assets/${asset.id}/review`, {});
      navigate(`/projects/${project.id}/items/${item.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="stack" style={{ gap: 16 }}>
      {canComment && (
        <div
          className={`dropzone ${over ? 'is-over' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          <input
            ref={input}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              void send(Array.from(event.target.files || []));
              event.target.value = '';
            }}
          />
          <Icon name="upload" className="icon-lg" />
          {progress ? (
            <>
              <strong style={{ color: 'var(--text-primary)' }}>
                Uploading {plural(progress.count, 'file')} · {Math.round(progress.fraction * 100)}%
              </strong>
              <div className="progress" style={{ width: 'min(320px, 100%)' }} role="progressbar" aria-valuenow={Math.round(progress.fraction * 100)} aria-valuemin={0} aria-valuemax={100}>
                <span style={{ width: `${progress.fraction * 100}%` }} />
              </div>
            </>
          ) : (
            <>
              <strong style={{ color: 'var(--text-primary)' }}>Drop files here to share them with the project</strong>
              <span>Logos, copy documents, photos, footage, anything the team needs. Up to {meta?.maxUploadMb ?? 1024} MB each.</span>
              <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                <input className="input" style={{ width: 260 }} placeholder="Add a note (optional)" aria-label="Note for these files" value={note} onChange={(event) => setNote(event.target.value)} />
                <button type="button" className="btn btn-primary" onClick={() => input.current?.click()}>
                  Choose files
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {error && (
        <div className="notice notice-error" role="alert">
          {error}
        </div>
      )}

      {loading && !data ? (
        <Spinner label="Loading files" />
      ) : loadError && !data ? (
        <ErrorState title="The files could not be loaded" message={loadError} onRetry={() => void reload()} />
      ) : files.length === 0 ? (
        <Empty icon="folder" title="No files yet">
          {canComment ? 'Anything uploaded here stays with the project, so nothing gets lost in email threads.' : 'Files shared on this project will show up here.'}
        </Empty>
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>File</th>
                <th>Size</th>
                <th>Uploaded by</th>
                <th>When</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {files.map((asset) => {
                const mine = !!user && asset.uploadedById === user.id;
                const menu: MenuEntry[] = [];
                if (canManage && asset.media !== 'other') menu.push({ label: 'Review with pins', icon: 'pin', onSelect: () => void startReview(asset) });
                if (canManage || mine) menu.push({ label: 'Delete', icon: 'trash', danger: true, onSelect: () => setRemoving(asset) });
                return (
                  <tr key={asset.id}>
                    <td style={{ maxWidth: 360 }}>
                      <div className="row">
                        <Icon name={iconFor(asset)} className="muted" />
                        <div style={{ minWidth: 0 }}>
                          <a href={asset.media === 'other' ? asset.downloadUrl : asset.url} target={asset.media === 'other' ? undefined : '_blank'} rel="noreferrer" className="truncate" style={{ display: 'block', color: 'var(--text-primary)', fontWeight: 500 }}>
                            {asset.filename}
                          </a>
                          {asset.note && <span className="muted truncate" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>{asset.note}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {fileSize(asset.size)}
                    </td>
                    <td className="muted">{asset.uploadedByName || 'Unknown'}</td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {dateTime(asset.createdAt)}
                    </td>
                    <td>
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 4 }}>
                        <a className="icon-btn" href={asset.downloadUrl} aria-label={`Download ${asset.filename}`} title="Download">
                          <Icon name="download" />
                        </a>
                        {menu.length > 0 && <Menu label={`Actions for ${asset.filename}`} trigger={<Icon name="more" />} items={menu} />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {removing && (
        <ConfirmDialog
          title={`Delete "${removing.filename}"?`}
          message="The file is removed for everyone on the project. This cannot be undone."
          confirmLabel="Delete file"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await api.del(`/api/assets/${removing.id}`);
              await reload(true);
              onChanged();
            } catch (err) {
              toast.error(errorMessage(err));
            }
          }}
        />
      )}
    </div>
  );
}
